import { memo, useEffect, useRef, useState } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { Grid, Box, useEventCallback } from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormRecapVM } from 'practice/view-models/pratice-recap';
import { PmiButton } from 'shell/components/shared/button';
import { FormikProps } from 'formik';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { HttpResponse } from 'shell/models/http-response';
import { transformResponseRequestCertificateGeneric } from 'front-office/utils/transform-response-certificate-generic';
import ErrorModal from './error-modal';
import { TypeRegister } from 'practice/constants/type-register';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { TypeInitiative } from 'practice/constants/practice-management-step';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import AttachRequestModal from './attach-request-modal';

interface StepRecapProps {
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  identifierAnpr?: string;
  typePractice?: string;
  formContext: FormikProps<FormRecapVM>;
  setDisabledForward: (disabled: boolean) => void;
  onSubmit: (values: FormRecapVM) => void;
  disabled?: boolean;
  fromPerson?: string;
}

const AccordionRecapFormComponent = ({
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  formContext,
  setDisabledForward,
  onSubmit,
  disabled,
  fromPerson,
}: StepRecapProps): React.ReactElement => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  const [successModal, setSuccessModal] = useState({ open: false, data: '', name: '' });

  const [modalWarning, setModalWarning] = useState<{ open: boolean; warnings: Array<string> }>({
    open: false,
    warnings: [],
  });

  // abilita "Allega domanda" solo dopo la produzione della domanda di apertura
  const [isDomandaProdotta, setIsDomandaProdotta] = useState(false);
  const [openAttachRequest, setOpenAttachRequest] = useState(false);

  const newRecap =
    TypeRegister.Immigration === typeRegister || TypeRegister.Residence === typeRegister;

  const updateData = useHttpClient<
    HttpResponse<ResponseCertificateGeneric>,
    HttpResponse<ResponseCertificateGeneric>
  >(`${__PRACTICE_API_URL__}/pratiche/riepilogo`, {
    options: { method: 'POST' },
    transformer: transformResponseRequestCertificateGeneric,
  });

  const updateDataNew = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/dichiarazione-residenza`,
    {
      options: { method: 'POST' },
    }
  );

  const onRecap = useEventCallback(() => {
    (
      updateData({
        data: {
          indirizzo: formContext.values.address || undefined,
          comune: formContext.values.municipality || undefined,
          provincia: formContext.values.province || undefined,
          numeroFax: formContext.values.fax || undefined,
          numTelefono: formContext.values?.telephone || undefined,
          numCellulare: formContext.values?.phone || undefined,
          email: formContext.values?.email || undefined,
          pec: formContext.values?.pec || undefined,
          noteContatto: formContext.values?.note || undefined,
          key: {
            codiceEnte: codeEnte || undefined,
            tipoRegistro: typeRegister || undefined,
            idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
            progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          },
        },
      }) as ResponsePromise<
        HttpResponse<ResponseCertificateGeneric>,
        HttpResponse<ResponseCertificateGeneric>
      >
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCertificateGeneric>;
      const pdfCertificate = responseCasted?.payload?.contenutoPdf;
      const warnings = responseCasted?.payload?.listaErrori;

      setDisabledForward(false);

      if (!warnings?.length) {
        if (pdfCertificate) {
          setPdfModal({ data: pdfCertificate, name: 'Riepilogo pratica', open: true });
        }
        return;
      }

      if (pdfCertificate) {
        setPdfModal({ data: pdfCertificate, name: 'Riepilogo pratica', open: false });
      }

      setModalWarning({ open: true, warnings });
    });
  });

  const onRecapNew = useEventCallback(() => {
    (
      updateDataNew({
        data: {
          numTelefono: formContext.values?.telephone || undefined,
          numCellulare: formContext.values?.phone || undefined,
          email: formContext.values?.email || undefined,
          pec: formContext.values?.pec || undefined,
          noteContatto: formContext.values?.note || undefined,
          indirizzo: formContext.values.address || undefined,
          provincia: formContext.values.province || undefined,
          comune: formContext.values.municipality || undefined,
          numeroFax: formContext.values.fax || undefined,
          key: {
            codiceEnte: codeEnte || undefined,
            tipoRegistro: typeRegister || undefined,
            idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
            progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
          },
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      const pdfCertificate = response?.data;

      setDisabledForward(false);

      if (pdfCertificate) {
        setIsDomandaProdotta(true);
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Riepilogo pratica',
            open: true,
          });
        });
      }
    });
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    // Se resid o immigr e aperta di ufficio posso avanzare senza riepilogo
    if (
      (typeRegister === TypeRegister.Immigration || typeRegister === TypeRegister.Residence) &&
      fromPerson === TypeInitiative.idUfficio
    ) {
      setDisabledForward(false);
    }
  }, [fromPerson, typeRegister]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <PmiTitleDivider>Modalità di contatto</PmiTitleDivider>
      </Grid>
      {typeRegister === TypeRegister.Immigration || typeRegister === TypeRegister.Residence ? (
        <Grid item xs={12} container spacing={1}>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Indirizzo"
              aria-label="Indirizzo"
              id="address"
              value={formContext.values?.address || ''}
              onChange={(event) => {
                formContext.setFieldValue('address', event.target.value);
              }}
              fullWidth
              error={formContext.touched?.address && Boolean(formContext.errors?.address)}
              helperText={formContext.errors?.address}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Comune"
              aria-label="Comune"
              id="municipality"
              value={formContext.values?.municipality || ''}
              onChange={(event) => {
                formContext.setFieldValue('municipality', event.target.value);
              }}
              fullWidth
              error={formContext.touched?.municipality && Boolean(formContext.errors?.municipality)}
              helperText={formContext.errors?.municipality}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              label="Provincia"
              aria-label="Provincia"
              id="province"
              value={formContext.values?.province || ''}
              onChange={(event) => {
                formContext.setFieldValue('province', event.target.value);
              }}
              fullWidth
              error={formContext.touched?.province && Boolean(formContext.errors?.province)}
              helperText={formContext.errors?.province}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      ) : undefined}
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Telefono"
            aria-label="Telefono"
            id="telephone"
            value={formContext.values?.telephone || ''}
            onChange={(event) => {
              formContext.setFieldValue('telephone', event.target.value);
            }}
            fullWidth
            inputProps={{
              maxLength: '50',
            }}
            error={formContext.touched?.telephone && Boolean(formContext.errors?.telephone)}
            helperText={formContext.errors?.telephone}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Cellulare"
            aria-label="Cellulare"
            id="phone"
            value={formContext.values?.phone || ''}
            onChange={(event) => {
              formContext.setFieldValue('phone', event.target.value);
            }}
            fullWidth
            inputProps={{
              maxLength: '50',
            }}
            error={formContext.touched?.phone && Boolean(formContext.errors?.phone)}
            helperText={formContext.errors?.phone}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiTextField
            label="Email"
            aria-label="Email"
            id="email"
            value={formContext.values?.email || ''}
            onChange={(event) => {
              formContext.setFieldValue('email', event.target.value);
            }}
            fullWidth
            inputProps={{
              maxLength: '254',
            }}
            error={formContext.touched?.email && Boolean(formContext.errors?.email)}
            helperText={formContext.errors?.email}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Pec"
            aria-label="Pec"
            id="pec"
            inputProps={{
              maxLength: '254',
            }}
            value={formContext.values?.pec || ''}
            onChange={(event) => {
              formContext.setFieldValue('pec', event.target.value);
            }}
            fullWidth
            error={formContext.touched?.pec && Boolean(formContext.errors?.pec)}
            helperText={formContext.errors?.pec}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PmiTextField
            label="Fax"
            aria-label="Fax"
            id="fax"
            inputProps={{
              maxLength: '254',
            }}
            value={formContext.values?.fax || ''}
            onChange={(event) => {
              formContext.setFieldValue('fax', event.target.value);
            }}
            fullWidth
            error={formContext.touched?.fax && Boolean(formContext.errors?.fax)}
            helperText={formContext.errors?.fax}
            disabled={disabled}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={1}>
        <Grid item xs={12}>
          <PmiTextField
            label="Note"
            aria-label="Note"
            id="note"
            inputProps={{
              maxLength: '255',
            }}
            value={formContext.values?.note || ''}
            onChange={(event) => {
              formContext.setFieldValue('note', event.target.value);
            }}
            fullWidth
            error={formContext.touched?.note && Boolean(formContext.errors?.note)}
            helperText={formContext.errors?.note}
            disabled={disabled}
            minRows={5}
            multiline
          />
        </Grid>
      </Grid>
      <Grid justifyContent={'space-evenly'} item container spacing={2}>
        <Grid item xs={12} md={3}>
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            id="practice-recap"
            fullWidth
            onClick={() => (newRecap ? onRecapNew() : onRecap())}
            disabled={
              fromPerson === TypeInitiative.idUfficio && typeRegister === TypeRegister.Emigration
            }
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {typeRegister === TypeRegister.Immigration || typeRegister === TypeRegister.Residence
                ? 'Domanda di apertura'
                : 'Riepilogo'}
            </Box>
          </PmiButton>
        </Grid>
        <Grid item xs={12} md={3}>
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            id="salva-bozza"
            fullWidth
            onClick={() => onSubmit(formContext.values)}
            disabled={disabled}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Salva Bozza
            </Box>
          </PmiButton>
        </Grid>
        {newRecap ? (
          <Grid item xs={12} md={3}>
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              id="allega-domanda"
              fullWidth
              onClick={() => setOpenAttachRequest(true)}
              disabled={disabled || !isDomandaProdotta}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                Allega domanda
              </Box>
            </PmiButton>
          </Grid>
        ) : undefined}
      </Grid>

      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Riepilogo Pratica"
        onClose={() => {
          setSuccessModal({ open: true, data: pdfModal.data, name: pdfModal.name });
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
      {successModal?.open ? (
        <ModalPrintSuccess
          open={successModal.open}
          onYes={() => {
            setSuccessModal({ open: false, data: '', name: '' });
          }}
          onClose={() => {
            setSuccessModal({ open: false, data: '', name: '' });
            setPdfModal({ data: successModal.data, open: true, name: successModal?.name });
          }}
        />
      ) : undefined}
      <ErrorModal
        open={modalWarning.open}
        onClose={() => {
          setModalWarning({ open: false, warnings: [] });
          setPdfModal((oldState) => ({ ...oldState, open: true }));
        }}
        warnings={modalWarning.warnings}
      />
      <AttachRequestModal
        open={openAttachRequest}
        onClose={() => setOpenAttachRequest(false)}
        codeEnte={codeEnte}
        yearId={yearId}
        typeRegister={typeRegister}
        progressive={progressive}
      />
    </Grid>
  );
};

AccordionRecapFormComponent.displayName = 'Accordion Recap Form';

const AccordionRecapForm = memo(AccordionRecapFormComponent);

export default AccordionRecapForm;
